import { 
    Box, 
    Typography, 
    Button, 
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material";
import moment from "moment";
import GetAppIcon from '@mui/icons-material/GetApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';

import { useGetLaborCostingReportQuery } from "../../state/api";

const LaborCosting = () => {
    const theme = useTheme();
    
    const [selectedPeriod, setSelectedPeriod] = useState({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD')
    });
    
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [activeTab, setActiveTab] = useState(0);
    
    const { data: costingData, isLoading, isError, refetch } = useGetLaborCostingReportQuery({
        ...selectedPeriod,
        department: selectedDepartment
    });

    const handlePeriodChange = (field, value) => {
        setSelectedPeriod(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExportData = () => {
        if (!costingData?.employeeDetails) return;
        
        const headers = ['Employee Name', 'Department', 'Total Hours', 'Regular Hours', 'Overtime Hours', 'Hourly Rate', 'Regular Cost', 'Overtime Cost', 'Total Cost'];
        const csvContent = [
            headers.join(','),
            ...costingData.employeeDetails.map(emp => [
                emp.employeeName,
                emp.department || 'N/A',
                emp.totalHours,
                emp.regularHours,
                emp.overtimeHours,
                emp.hourlyRate,
                emp.regularCost,
                emp.overtimeCost,
                emp.totalCost
            ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `labor-costing-${selectedPeriod.startDate}-to-${selectedPeriod.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const departments = costingData?.departmentSummary?.map(dept => dept.department) || [];

    if (isLoading) {
        return <Typography>Loading labor costing report...</Typography>;
    }

    if (isError) {
        return <Typography color="error">Error loading labor costing data.</Typography>;
    }

    return (
        <Box m="1.5rem 2.5rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="2rem">
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Labor Costing Report
                </Typography>
                <Box display="flex" gap="1rem" alignItems="center">
                    <TextField
                        label="Start Date"
                        type="date"
                        value={selectedPeriod.startDate}
                        onChange={(e) => handlePeriodChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={selectedPeriod.endDate}
                        onChange={(e) => handlePeriodChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            label="Department"
                        >
                            <MenuItem value="all">All Departments</MenuItem>
                            {departments.map(dept => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        onClick={() => refetch()}
                        sx={{ color: theme.palette.secondary[100], borderColor: theme.palette.secondary[100] }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<GetAppIcon />}
                        onClick={handleExportData}
                        sx={{
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.background.alt,
                        }}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb="2rem">
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <GroupIcon color="primary" />
                                <Box>
                                    <Typography variant="h6" color="primary">
                                        {costingData?.summary?.totalEmployees || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Active Employees
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                {/*<AttachMoneyIcon color="success" /> */}
                                <Box>
                                    <Typography variant="h6" color="success.main">
                                        £{costingData?.summary?.grandTotalCost || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Labor Cost
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <TrendingUpIcon color="secondary" />
                                <Box>
                                    <Typography variant="h6" color="secondary">
                                        {costingData?.summary?.totalRegularHours || 0}h
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Regular Hours
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <TrendingUpIcon color="warning" />
                                <Box>
                                    <Typography variant="h6" color="warning.main">
                                        {costingData?.summary?.totalOvertimeHours || 0}h
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Overtime Hours
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Cost Breakdown Cards */}
            <Grid container spacing={3} mb="2rem">
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Regular Cost</Typography>
                            <Typography variant="h4" color="success.main">
                                £{costingData?.summary?.totalRegularCost || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {costingData?.summary?.totalRegularHours || 0} hours @ regular rates
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Overtime Cost</Typography>
                            <Typography variant="h4" color="warning.main">
                                £{costingData?.summary?.totalOvertimeCost || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {costingData?.summary?.totalOvertimeHours || 0} hours @ overtime rates
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Overtime %</Typography>
                            <Typography variant="h4" color="info.main">
                                {costingData?.summary?.totalRegularHours > 0 ? 
                                    ((costingData?.summary?.totalOvertimeHours / (costingData?.summary?.totalRegularHours + costingData?.summary?.totalOvertimeHours)) * 100).toFixed(1) : 0}%
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Of total hours worked
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs for Employee and Department Views */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Employee Details" />
                    <Tab label="Department Summary" />
                </Tabs>

                {activeTab === 0 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Total Hours</TableCell>
                                    <TableCell align="right">Regular Hours</TableCell>
                                    <TableCell align="right">Overtime Hours</TableCell>
                                    <TableCell align="right">Hourly Rate</TableCell>
                                    <TableCell align="right">Regular Cost</TableCell>
                                    <TableCell align="right">Overtime Cost</TableCell>
                                    <TableCell align="right">Total Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {costingData?.employeeDetails?.map((employee) => (
                                    <TableRow key={employee.employeeId} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {employee.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={employee.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {employee.totalHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                {employee.regularHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color={employee.overtimeHours > 0 ? "warning.main" : "textSecondary"}>
                                                {employee.overtimeHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                £{employee.hourlyRate}/hr
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                £{employee.regularCost}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color={employee.overtimeCost > 0 ? "warning.main" : "textSecondary"}>
                                                £{employee.overtimeCost}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body1" fontWeight="bold" color="primary">
                                                £{employee.totalCost}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {activeTab === 1 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Employees</TableCell>
                                    <TableCell align="right">Total Hours</TableCell>
                                    <TableCell align="right">Regular Cost</TableCell>
                                    <TableCell align="right">Overtime Cost</TableCell>
                                    <TableCell align="right">Total Cost</TableCell>
                                    <TableCell align="right">Avg Cost/Employee</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {costingData?.departmentSummary?.map((department) => (
                                    <TableRow key={department.department} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {department.department}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip 
                                                label={department.employeeCount} 
                                                size="small" 
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {department.totalHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                ${department.regularCost}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color={department.overtimeCost > 0 ? "warning.main" : "textSecondary"}>
                                                ${department.overtimeCost}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body1" fontWeight="bold" color="primary">
                                                ${department.totalCost}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                ${department.employeeCount > 0 ? (department.totalCost / department.employeeCount).toFixed(2) : 0}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default LaborCosting; 